/**
 * Mascara de documento e de contato, para EXIBICAO.
 *
 * O valor cru continua sendo o do banco — so digito, sem pontuacao. Quem mostra
 * mascara; quem copia, compara ou envia usa o cru. E por isso que `CopyableCpfCnpj`
 * recebe os dois: mostra `123.456.789-09` e coloca `12345678909` na area de
 * transferencia, que e o que o outro sistema espera receber.
 *
 * Nada aqui valida. Mascarar e vestir; validar e outra conversa, e ela pertence ao
 * formulario e a API (CLAUDE.md, "Regras de API", item 2).
 */

/** So os digitos. Serve tanto para mascarar quanto para montar busca. */
export function somenteDigitos(valor: string): string {
  return valor.replace(/\D/g, '');
}

/** `123.456.789-09` */
export function mascaraCpf(valor: string): string {
  return somenteDigitos(valor)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

/** `12.345.678/0001-95` */
export function mascaraCnpj(valor: string): string {
  return somenteDigitos(valor)
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

/**
 * Decide pelo tamanho, nao por um campo `tipo` a mais.
 *
 * Onze digitos e CPF, quatorze e CNPJ — a coluna `cpf_cnpj` guarda os dois, e
 * cobrar o tipo junto faria toda celula de tabela ter de carregar duas
 * informacoes para mostrar uma.
 */
export function mascaraCpfCnpj(valor: string | null | undefined): string {
  if (!valor) {
    return '';
  }
  const digitos = somenteDigitos(valor);
  if (digitos.length === 0) {
    return valor;
  }
  return digitos.length <= 11 ? mascaraCpf(digitos) : mascaraCnpj(digitos);
}

/** `(11) 98765-4321` ou `(11) 3456-7890`. Numero de tamanho inesperado sai como veio. */
export function mascaraTelefone(valor: string | null | undefined): string {
  if (!valor) {
    return '';
  }
  const digitos = somenteDigitos(valor);
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return valor;
}

/** `01234-567` */
export function mascaraCep(valor: string | null | undefined): string {
  if (!valor) {
    return '';
  }
  return somenteDigitos(valor)
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, '$1-$2');
}

/**
 * `ABC-1234` no padrao antigo, `ABC1D23` no Mercosul.
 *
 * A placa Mercosul tem letra na quinta posicao. Enfiar hifen nela produziria
 * `ABC-1D23`, que nao existe em documento nenhum — e placa e justamente o campo
 * que o fiscal confere contra o papel.
 */
export function mascaraPlaca(valor: string | null | undefined): string {
  if (!valor) {
    return '';
  }
  const limpa = valor.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (limpa.length <= 3) {
    return limpa;
  }
  const ehMercosul = limpa.length === 7 && /[A-Z]/.test(limpa[4] ?? '');
  if (ehMercosul) {
    return limpa;
  }
  return `${limpa.slice(0, 3)}-${limpa.slice(3, 7)}`;
}
